/* eslint-disable */
import * as THREE from '../../../libs/three.js/build/three.module.js';
import { EventDispatcher } from '../../EventDispatcher.js';
import { ClipMethod, ClipTask } from '../../defines.js';
import { BoxVolume } from '../../utils/Volume.js';
import { Images360UIMixin } from './Images360UI.js';
import {
  radius,
  sphereGeometry,
  sphereGeometryHighlighted,
  sphereMaterial,
  sphereMarkerMaterial,
  sphereMaterialHovered,
  raycaster,
  getCurrentlyHovered, setCurrentlyHovered,
  getPreviouslyHovered, setPreviouslyHovered,
  getCurrentlyFocused, setCurrentlyFocused,
  isHovering, setHovering,
  getCourseLocal, setCourseLocal,
  getPitchLocal, setPitchLocal,
  getRollLocal, setRollLocal,
  getPosXLocal, setPosXLocal,
  getPosYLocal, setPosYLocal,
  getPosZLocal, setPosZLocal,
  resetLocalValues,
  getPreviousView, setPreviousView,
  getImage360Accepted, addToImage360Accepted,
  replaceInImage360Accepted, clearImage360Accepted,
  isInImage360Accepted, indexInImage360Accepted,
} from './constants.js';

/**
 * Klasa bazowa z EventDispatcher
 */
class Images360Base extends EventDispatcher {
  constructor() {
    super();
  }
}

/**
 * Główna klasa zarządzająca panoramami 360°
 * Łączy funkcjonalność bazową z UI poprzez mixin
 */
export class Images360 extends Images360UIMixin(Images360Base) {
  constructor(viewer, callbacks = null) {
    super();
    this._callbacks = callbacks;

    this.viewer = viewer;
    this.sidebarVisible = viewer.sidebarVisible;
    this.editMode = true;
    this.isMobile = viewer.isMobileDevice ? viewer.isMobileDevice() : false;
    this.selectingEnabled = true;
    this._editMode = false;
    this._renderDistance = 150;
    this.currentRadius = radius;

    this.images = [];
    this.imagesSourceUnique = [];
    this.node = new THREE.Object3D();


    this.sphere = new THREE.Mesh(sphereGeometryHighlighted, sphereMaterial);
    this.sphere.visible = false;
    this.sphere.scale.set(1000, 1000, 1000);
    this.node.add(this.sphere);

    this._visible = [];
    this.annotations = this.viewer.scene.annotations.children;
    this.focusedImage = null;
    this._ownLastHovered = null;
    this._pendingDeltas = new Map();
    this.floorMax = undefined;
    this._clipTestVec = new THREE.Vector3();

    // Floor detection
    if (this.viewer.mapView && this.viewer.mapView.map) {
    this.viewer.mapView.map.addEventListener('floors-loaded', (e) => {
      let floorsZCeiling = e.floorsZCeiling;
      if (this.images.length === 0) return;

      for (let img of this.images) {
        for (let i = floorsZCeiling.length - 1; i >= 0; i--) {
          if (img.altitude > floorsZCeiling[i]) {
            img.floor = i + 1;
            if (img.floor > this.floorMax || this.floorMax === undefined) {
              this.floorMax = img.floor;
            }
            break;
          } else if (img.altitude < floorsZCeiling[i] && i === 0) {
            img.floor = 0;
            if (img.floor > this.floorMax || this.floorMax === undefined) {
              this.floorMax = img.floor;
            }
            break;
          }
        }
      }
    });
    }

    // FOV change handler — skip when scrolling inside the map overlay
    const mapEl = document.getElementById('potree_map');
    const changeFOV = (e) => {
      if (isHovering()) {
        if (mapEl && mapEl.contains(e.target)) return;
        let fov = this.viewer.getFOV();
        if (e.deltaY > 0 && fov <= 100) {
          this.viewer.setFOV(++fov);
        } else if (fov >= 0) {
          this.viewer.setFOV(--fov);
        }
      }
    };

    // Initialize DOM root
    this.domRoot = viewer.renderer.domElement.parentElement;

    // Initialize UI components
    const { popup, header } = this.initPopup(viewer, changeFOV);
    this.initBasicButtons(viewer);
    this.initEditButtons(viewer);
    this.initEditControls(viewer);
    this.initPopupDragging(popup, header);

    // React to edit rights from host application or standalone checkbox
    viewer.addEventListener('edit_rights_changed', (e) => {
      if (e.canEdit) {
        this.setEditMode(true);
      }
    });

    // React to edit mode toggle from UI panel
    viewer.addEventListener('panorama_edit_mode_changed', (e) => {
      this.setEditMode(e.value);
    });

    // React to render distance slider from Appearance dialog
    viewer.addEventListener('panorama_render_distance_changed', (e) => {
      this.setRenderDistance(e.value);
    });

    // React to sphere size slider from Appearance dialog
    viewer.addEventListener('panorama_sphere_size_changed', (e) => {
      this.setSphereSize(e.value);
    });

    // React to rotation changes from sidebar edit panel
    viewer.addEventListener('panorama_rotation_changed', (e) => {
      if (!isHovering()) return;
      let val = parseFloat(e.value) || 0;
      if (val > 180) val = 180;
      if (val < -180) val = -180;

      if (e.axis === 'roll') setRollLocal(val);
      else if (e.axis === 'pitch') setPitchLocal(val);
      else if (e.axis === 'course') setCourseLocal(val);

      if (this.focusedImage) {
        this.sphere.rotation.set(
          THREE.MathUtils.degToRad(+this.focusedImage.roll + 90 + getRollLocal()),
          THREE.MathUtils.degToRad(-this.focusedImage.pitch + getPitchLocal()),
          THREE.MathUtils.degToRad(-this.focusedImage.course + 90 + getCourseLocal()),
          'ZYX'
        );
      }
    });

    // React to position changes from sidebar edit panel
    viewer.addEventListener('panorama_position_changed', (e) => {
      if (!isHovering()) return;
      const val = parseFloat(e.value) || 0;

      if (e.axis === 'X') setPosXLocal(val);
      else if (e.axis === 'Y') setPosYLocal(val);
      else if (e.axis === 'Z') setPosZLocal(val);

      this.updateSpherePosition();
    });

    // Update event
    viewer.addEventListener('update', () => {
      this.update(viewer);
    });

    // Mouse click handler — route to the instance that owns the hovered image
    this.addEventListener('mousedown', (event) => {
      document.onmousedown = (e) => {
        if (event != null && event != undefined) {
          if (e.button === THREE.MOUSE.LEFT) {
            const currentlyHovered = getCurrentlyHovered();
            if (currentlyHovered && currentlyHovered.image360) {
              const targetImage = currentlyHovered.image360;
              const ownerInstance = this.viewer.scene.images360.find(
                inst => inst.images.includes(targetImage)
              ) || this;

              if (ownerInstance.focusedImage !== null) {
                ownerInstance._savePendingDelta(ownerInstance.focusedImage);
                ownerInstance._switchingPanoramas = true;
                ownerInstance.unfocus();
                ownerInstance._switchingPanoramas = false;
                ownerInstance.handleFocus(targetImage);
              } else {
                ownerInstance.focus(targetImage);
              }
              setCurrentlyHovered(null);
            }
          }
        }
      };
    });

    // Touch handler for mobile — route to the instance that owns the hovered image
    this.addEventListener('touchend', (e) => {
      const currentlyHovered = getCurrentlyHovered();

      if (currentlyHovered && currentlyHovered.image360) {
        const targetImage = currentlyHovered.image360;
        const ownerInstance = this.viewer.scene.images360.find(
          inst => inst.images.includes(targetImage)
        ) || this;

        ownerInstance.unfocus();
        ownerInstance.focus(targetImage);
        setCurrentlyHovered(null);
        return;
      }

      if (e.changedTouches && e.changedTouches.length > 0) {
        let touch = e.changedTouches[0];
        let rect = this.viewer.renderer.domElement.getBoundingClientRect();
        let x = touch.pageX - rect.left;
        let y = touch.pageY - rect.top;
        let mouse = new THREE.Vector2(x, y);

        let camera = this.viewer.scene.getActiveCamera();
        let domElement = this.viewer.renderer.domElement;

        let ray = Potree.Utils.mouseToRay(
          mouse,
          camera,
          domElement.clientWidth,
          domElement.clientHeight
        );

        raycaster.ray.copy(ray);
        let intersections = raycaster.intersectObjects(this.node.children);
        let checkedIntersections = intersections.filter(d => d.object.name !== '');

        if (checkedIntersections.length > 0) {
          let intersection = checkedIntersections[0];

          if (isHovering() && checkedIntersections.length > 1) {
            intersection = checkedIntersections[1];
          }

          let tappedObject = intersection.object;

          if (tappedObject.image360 && tappedObject.image360.mesh.visible) {
            if (!(isHovering() && checkedIntersections.length === 1)) {
              setCurrentlyHovered(tappedObject);
              this.unfocus();
              this.focus(tappedObject.image360);
              setCurrentlyHovered(null);
            }
          }
        }
      }
    });

    viewer.inputHandler.addInputListener(this);

    // Escape key to exit panorama
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isHovering()) {
        this.unfocus();
      }
    });

    // FOV wheel event
    const potreeRenderArea = document.getElementById('potree_render_area');
    if (potreeRenderArea) potreeRenderArea.addEventListener('wheel', changeFOV);

    // Floor changed event
    this.addEventListener('floor-changed', (e) => {
      if (e.floor === null || e.floor === undefined) return;

      if (e.floor > this.floorMax) {
        for (let image of this.images) {
          this.dispatchEvent({ type: 'unhide-image', image: image });
        }
      } else {
        for (let image of this.images) {
          if (image.floor != e.floor) {
            this.dispatchEvent({ type: 'hidden-image', image: image });
          } else {
            this.dispatchEvent({ type: 'unhide-image', image: image });
          }
        }
      }
    });

    if (!this.isMobile) {
      this.displayControls(this._editMode);
    }
  }

  /**
   * Active clipping boxes for point clouds (same sources as Viewer.update):
   * clip-enabled BoxVolumes plus profile segment boxes.
   */
  _collectActiveClipBoxInverses() {
    const scene = this.viewer.scene;
    const boxes = [];
    boxes.push(...scene.volumes.filter((v) => v.clip && v instanceof BoxVolume));
    for (const profile of scene.profiles) {
      boxes.push(...profile.boxes);
    }
    const inverses = [];
    for (const box of boxes) {
      box.updateMatrixWorld();
      if (box.matrixWorld.determinant() === 0) {
        continue;
      }
      inverses.push(box.matrixWorld.clone().invert());
    }
    return inverses;
  }

  /**
   * World-space position allowed for panoramas under current clip settings
   * (mirrors pointcloud.vs clipMethod / clipTask for box clip volumes only).
   */
  _worldPosPassesClipVolumes(worldPos, inverseMatrices) {
    if (inverseMatrices.length === 0) {
      return true;
    }
    const task = this.viewer.clipTask;
    if (task === ClipTask.NONE) {
      return true;
    }

    // Domyślny tryb panelu przycinania to HIGHLIGHT — chmura zostaje w całości, tylko kolor.
    // Markery panoram ukrywamy poza objętością tak jak przy "Pokaż wewnątrz".
    const effectiveTask =
      task === ClipTask.HIGHLIGHT ? ClipTask.SHOW_INSIDE : task;

    let insideCount = 0;
    for (const inv of inverseMatrices) {
      this._clipTestVec.copy(worldPos).applyMatrix4(inv);
      const p = this._clipTestVec;
      const inside =
        p.x >= -0.5 &&
        p.x <= 0.5 &&
        p.y >= -0.5 &&
        p.y <= 0.5 &&
        p.z >= -0.5 &&
        p.z <= 0.5;
      if (inside) {
        insideCount++;
      }
    }
    const clipVolumesCount = inverseMatrices.length;
    const insideAny = insideCount > 0;
    const insideAll = clipVolumesCount > 0 && insideCount === clipVolumesCount;
    const method = this.viewer.clipMethod;

    if (method === ClipMethod.INSIDE_ANY) {
      if (effectiveTask === ClipTask.SHOW_INSIDE) {
        return insideAny;
      }
      if (effectiveTask === ClipTask.SHOW_OUTSIDE) {
        return !insideAny;
      }
    } else {
      if (effectiveTask === ClipTask.SHOW_INSIDE) {
        return insideAll;
      }
      if (effectiveTask === ClipTask.SHOW_OUTSIDE) {
        return !insideAll;
      }
    }
    return true;
  }

  /**
   * Po włączeniu warstwy w panelu Zawartość — przywróć widoczność kulek wg klipu / ukrytych.
   */
  refreshLayerMarkerVisibility() {
    if (isHovering()) {
      this.renderSpheres();
    } else {
      this._syncIdlePanoramaMarkerVisibility();
    }
  }

  /** Public: whether a panorama station is inside the active clip (for loaders / UI). */
  panoramaAllowedByActiveClip(image) {
    if (!image || !image.position) {
      return true;
    }
    const worldPos = this._clipTestVec.set(
      image.position[0],
      image.position[1],
      image.position[2]
    );
    return this._worldPosPassesClipVolumes(
      worldPos,
      this._collectActiveClipBoxInverses()
    );
  }

  _syncIdlePanoramaMarkerVisibility() {
    if (isHovering()) {
      return;
    }
    if (!this.node.visible) {
      return;
    }
    const isEditModeOn = this.getEditModeChecked();
    const currentlyHovered = getCurrentlyHovered();
    const inverses = this._collectActiveClipBoxInverses();

    for (const image of this.images) {
      if (image.mesh === currentlyHovered) {
        continue;
      }
      let base = false;
      if (!isEditModeOn && image.isHidden) {
        base = false;
      } else if (isEditModeOn && image.isHidden) {
        base = true;
      } else {
        base = true;
      }
      const worldPos = this._clipTestVec.set(
        image.position[0],
        image.position[1],
        image.position[2]
      );
      image.mesh.visible =
        base && this._worldPosPassesClipVolumes(worldPos, inverses);
    }
  }

  // Visibility getter/setter
  set visible(visible) {
    if (this._visible === visible) return;

    let checked = this.getEditModeChecked();

    if (visible.constructor === Array) {
      for (const image of this.images) {
        for (let i = 0; i < this.imagesSourceUnique.length; i++) {
          if (image.name === this.imagesSourceUnique[i]) {
            image.mesh.visible =
              visible[i] &&
              (!image.isHidden || checked) &&
              this.panoramaAllowedByActiveClip(image);
          }
        }
      }
      for (let i = 0; i < visible.length; i++) {
        this._visible[i] = visible[i];
      }
    } else {
      for (const image of this.images) {
        image.mesh.visible =
          visible &&
          (!image.isHidden || checked) &&
          this.panoramaAllowedByActiveClip(image);
      }
      for (let i = 0; i < this._visible.length; i++) {
        this._visible[i] = visible;
      }
    }

    if (isHovering()) {
      getCurrentlyFocused().mesh.visible = false;
    }
  }

  get visible() {
    return this._visible;
  }

  // Show hidden images
  showHidden() {
    for (let image of this.images) {
      image.isHidden = false;
    }
    if (isHovering()) {
      this.renderSpheres();
    } else {
      for (let image of this.images) {
        image.mesh.visible = this.panoramaAllowedByActiveClip(image);
      }
    }
  }

  // Update sphere position based on local offsets
  updateSpherePosition() {
    if (!this.focusedImage) return;

    const curOffset = new THREE.Vector3(getPosXLocal(), getPosYLocal(), getPosZLocal());
    const prevOffset = this._lastPosOffset || new THREE.Vector3();
    const delta = curOffset.clone().sub(prevOffset);
    this._lastPosOffset = curOffset.clone();

    this.sphere.position.set(
      this.focusedImage.position[0] + curOffset.x,
      this.focusedImage.position[1] + curOffset.y,
      this.focusedImage.position[2] + curOffset.z
    );

    if (delta.lengthSq() === 0) return;

    const camPos = this.viewer.scene.view.position;
    const target = this.viewer.scene.view.getPivot();
    camPos.add(delta);
    this.viewer.scene.view.lookAt(target.clone().add(delta));
  }

  _savePendingDelta(image) {
    if (!image) return;
    const delta = {
      roll: getRollLocal(), pitch: getPitchLocal(), course: getCourseLocal(),
      posX: getPosXLocal(), posY: getPosYLocal(), posZ: getPosZLocal(),
    };
    const hasChange = Object.values(delta).some(v => v !== 0);
    if (hasChange) {
      this._pendingDeltas.set(image, delta);
    } else {
      this._pendingDeltas.delete(image);
    }
  }

  _restorePendingDelta(image) {
    const delta = image ? this._pendingDeltas.get(image) : null;
    if (delta) {
      setRollLocal(delta.roll);
      setPitchLocal(delta.pitch);
      setCourseLocal(delta.course);
      setPosXLocal(delta.posX);
      setPosYLocal(delta.posY);
      setPosZLocal(delta.posZ);
    }
  }

  hasPendingDelta(image) {
    return this._pendingDeltas.has(image);
  }

  _clearPendingDelta(image) {
    if (image) this._pendingDeltas.delete(image);
  }

  async saveAll() {
    const entries = [...this._pendingDeltas.entries()];
    const wasFocused = getCurrentlyFocused();
    const wasHovering = isHovering();

    for (const [image, delta] of entries) {
      setRollLocal(delta.roll);
      setPitchLocal(delta.pitch);
      setCourseLocal(delta.course);
      setPosXLocal(delta.posX);
      setPosYLocal(delta.posY);
      setPosZLocal(delta.posZ);
      setCurrentlyFocused(image);
      setHovering(true);
      await this.acceptRotation();
    }
    this._pendingDeltas.clear();

    if (wasFocused) setCurrentlyFocused(wasFocused);
    setHovering(wasHovering);
    this.viewer.dispatchEvent({ type: 'panorama_deltas_changed' });
  }

  // Reset rotation and position values
  resetRotValues() {
    resetLocalValues();
    this._lastPosOffset = null;

    if (!this.isMobile) {
      if (this.xInput) this.xInput.value = 0;
      if (this.yInput) this.yInput.value = 0;
      if (this.zInput) this.zInput.value = 0;
      if (this.axisXPosRot) this.axisXPosRot.value = 0;
      if (this.axisYPosRot) this.axisYPosRot.value = 0;
      if (this.axisZPosRot) this.axisZPosRot.value = 0;
      if (this.xPosInput) this.xPosInput.value = 0;
      if (this.yPosInput) this.yPosInput.value = 0;
      if (this.zPosInput) this.zPosInput.value = 0;
      if (this.axisXPos) this.axisXPos.value = 0;
      if (this.axisYPos) this.axisYPos.value = 0;
      if (this.axisZPos) this.axisZPos.value = 0;
    }

    this.updateSpherePosition();
  }

  // Calculate distance between images
  calcDistance(focusedImage, image) {
    let focusedImageVector = new THREE.Vector3(
      focusedImage.position[0],
      focusedImage.position[1],
      focusedImage.position[2]
    );
    let imageVector = new THREE.Vector3(
      image.position[0],
      image.position[1],
      image.position[2]
    );
    return focusedImageVector.distanceTo(imageVector);
  }

  // Calculate distance to annotation
  calcDistanceAnnotation(focusedImage, annotation) {
    let focusedImageVector = new THREE.Vector3(
      focusedImage.position[0],
      focusedImage.position[1],
      focusedImage.position[2]
    );
    return focusedImageVector.distanceTo(annotation.position);
  }

  // Render spheres within distance
  renderSpheres() {
    if (!this.node.visible) {
      return;
    }
    const renderDistanceValue = this.getRenderDistance();
    const currentlyFocused = getCurrentlyFocused();
    const clipInverses = this._collectActiveClipBoxInverses();

    for (let image of this.images) {
      const inRange =
        this.calcDistance(currentlyFocused, image) <= renderDistanceValue &&
        image !== currentlyFocused &&
        !image.isHidden;
      const worldPos = this._clipTestVec.set(
        image.position[0],
        image.position[1],
        image.position[2]
      );
      if (inRange && this._worldPosPassesClipVolumes(worldPos, clipInverses)) {
        image.mesh.visible = true;
      } else {
        image.mesh.visible = false;
      }
    }
    for (let annotation of this.annotations) {
      if (this.calcDistanceAnnotation(currentlyFocused, annotation) <= renderDistanceValue) {
        annotation.visible = true;
      } else {
        annotation.visible = false;
      }
    }
  }

  // Handle focus on image
  handleFocus(image360) {
    if (!this.node.visible) {
      return;
    }
    const clipInverses = this._collectActiveClipBoxInverses();
    if (
      !this._worldPosPassesClipVolumes(
        this._clipTestVec.set(
          image360.position[0],
          image360.position[1],
          image360.position[2]
        ),
        clipInverses
      )
    ) {
      return;
    }

    // Unfocus any panorama in a *different* Images360 instance first.
    // Inherit its saved EDL opacity so we restore the true pre-panorama value on exit.
    for (const other of this.viewer.scene.images360) {
      if (other !== this && other.focusedImage !== null) {
        this._edlOpacityBeforeFocus = other._edlOpacityBeforeFocus;
        other._savePendingDelta(other.focusedImage);
        other._switchingPanoramas = true;
        other.unfocus();
        other._switchingPanoramas = false;
      }
    }

    if (this.focusedImage !== null) {
      this._savePendingDelta(this.focusedImage);
      this._switchingPanoramas = true;
      this.unfocus();
      this._switchingPanoramas = false;
    }

    setHovering(true);
    setCurrentlyFocused(image360);

    this.viewer.mapView.map.dispatchEvent({
      type: 'floor-display',
      floor: image360.floor,
    });

    for (let image of this.images) {
      if (image.floor != image360.floor) {
        this.dispatchEvent({ type: 'hidden-image', image: image });
      } else {
        this.dispatchEvent({ type: 'unhide-image', image: image });
      }
    }

    this.resetRotValues();

    // Only capture EDL if we didn't already inherit it from another instance
    if (this._edlOpacityBeforeFocus == null) {
      this._edlOpacityBeforeFocus = this.viewer.getEDLOpacity();
    }
    let checked = this.getEditModeChecked();
    if (!checked) {
      this.viewer.setEDLOpacity(0);
    }

    // Only save previous view when entering panorama from outside (not when switching)
    if (!this._switchingPanoramas) {
      setPreviousView({
        controls: this.viewer.controls,
        position: this.viewer.scene.view.position.clone(),
        target: this.viewer.scene.view.getPivot(),
      });
    }

    this.viewer.setControls(this.viewer.orbitControls);
    this.viewer.orbitControls.doubleClockZoomEnabled = false;
    this.viewer.orbitControls.scrollEnabled = false;

    image360.mesh.visible = false;
    this.renderSpheres();
    this.handleSavedArr();
    this.selectingEnabled = false;
    this.sphere.visible = false;

    this.load(image360).then(() => {
      if (!isHovering() || this.focusedImage !== image360) {
        return;
      }
      this.sphere.material.map = image360.texture;
      this.sphere.material.needsUpdate = true;
      // Delay visibility by one frame so Three.js processes the texture update
      // before the sphere is rendered (avoids mapSize[0] race condition).
      requestAnimationFrame(() => {
        if (!isHovering() || this.focusedImage !== image360) {
          return;
        }
        this.sphere.visible = true;
      });
    }).catch((err) => {
      console.error('[Images360] Error loading panorama:', err);
    });

    {
      let { course, pitch, roll } = image360;
      this._restorePendingDelta(image360);
      this.sphere.rotation.set(
        THREE.MathUtils.degToRad(+roll + 90 + getRollLocal()),
        THREE.MathUtils.degToRad(-pitch + getPitchLocal()),
        THREE.MathUtils.degToRad(-course + 90 + getCourseLocal()),
        'ZYX'
      );
    }

    this.sphere.position.set(
      image360.position[0] + getPosXLocal(),
      image360.position[1] + getPosYLocal(),
      image360.position[2] + getPosZLocal()
    );

    let target = new THREE.Vector3(...image360.position);
    let dir = target.clone().sub(this.viewer.scene.view.position).normalize();
    let move = dir.multiplyScalar(0.000001);
    let newCamPos = target.clone().sub(move);

    this.viewer.scene.view.setView(newCamPos, target, 500);
    this.focusedImage = image360;
    
    // Ensure displayControls is called after focus to update UI
    setTimeout(() => {
      this.displayControls(this.getEditModeChecked());
    }, 100);
  }

  // Focus on image (from click)
  focus(image360) {
    this.viewer.setFOV(60);
    if (getCurrentlyHovered() === null) return;

    const popupEl = document.getElementById('popup');
    if (popupEl && popupEl.style.display !== 'none') {
      window.location.href = '#' + image360.file;
      popupEl.scrollTop -= 90;
      const imageEl = document.getElementById(image360.file);
      if (imageEl) imageEl.classList.add('hovered');
    }
    this.handleFocus(image360);
    this.displayControls(this.getEditModeChecked());
  }

  // Focus from point
  focusPoint(image360, feature) {
    this.viewer.setFOV(60);
    if (feature === null) return;

    const popupEl = document.getElementById('popup');
    if (popupEl && popupEl.style.display !== 'none') {
      window.location.href = '#' + image360.file;
      popupEl.scrollTop -= 90;
      const imageEl = document.getElementById(image360.file);
      if (imageEl) imageEl.classList.add('hovered');
    }
    this.handleFocus(image360);
    this.displayControls(this.getEditModeChecked());
  }

  // Focus from list
  focusList(image360) {
    this.viewer.setFOV(60);
    if (image360 === null) return;

    const popupEl = document.getElementById('popup');
    if (popupEl && popupEl.style.display !== 'none') {
      window.location.href = '#' + image360.file;
      popupEl.scrollTop -= 90;
      const imageEl = document.getElementById(image360.file);
      if (imageEl) imageEl.classList.add('hovered');
    }
    this.handleFocus(image360);
    this.displayControls(this.getEditModeChecked());
  }

  // Focus from HTML
  focusHTML(image360, param) {
    if (image360 === null || (param === '' && param != null)) return;

    const popupEl = document.getElementById('popup');
    if (popupEl && popupEl.style.display !== 'none') {
      window.location.href = '#' + image360.file;
      popupEl.scrollTop -= 90;
      const imageEl = document.getElementById(image360.file);
      if (imageEl) imageEl.classList.add('hovered');
    }
    this.handleFocus(image360);
    this.displayControls(this.getEditModeChecked());
  }

  // Unfocus current image
  unfocus() {
    this.viewer.setFOV(60);
    setHovering(false);
    this.resetRotValues();

    const switching = this._switchingPanoramas;

    if (!switching) {
      this.selectingEnabled = true;
      this.viewer.setEDLOpacity(this._edlOpacityBeforeFocus ?? 1.0);
      this._edlOpacityBeforeFocus = null;

      for (let image of this.images) {
        if (!image.isHidden) {
          image.mesh.visible = this.panoramaAllowedByActiveClip(image);
        }
      }
      for (let annotation of this.annotations) {
        annotation.visible = true;
      }

      this.handleSavedArr();
    }

    let image = this.focusedImage;
    if (image === null) return;

    this.sphere.material.map = null;
    this.sphere.material.needsUpdate = true;
    this.sphere.visible = false;

    if (!switching) {
      this.viewer.orbitControls.doubleClockZoomEnabled = true;
      this.viewer.orbitControls.scrollEnabled = true;

      const previousView = getPreviousView();
      this.viewer.setControls(previousView.controls);

      this.viewer.scene.view.setView(
        this.viewer.scene.view.position.add(new THREE.Vector3(0.0, 0.0, 0.5)),
        this.viewer.scene.view.getPivot(),
        500
      );

      this.viewer.setMoveSpeed(2.0);
    }

    const popupEl = document.getElementById('popup');
    if (popupEl && popupEl.style.display !== 'none' && this.focusedImage) {
      const imageEl = document.getElementById(this.focusedImage.file);
      if (imageEl) imageEl.classList.remove('hovered');
    }

    this.focusedImage = null;
    setPreviouslyHovered(null);
    this.displayControls(this.getEditModeChecked());
  }

  // Delete current view
  deleteView() {
    if (!isHovering()) return;
    const currentlyFocused = getCurrentlyFocused();
    currentlyFocused.toDelete = true;

    if (isInImage360Accepted(currentlyFocused)) {
      const index = indexInImage360Accepted(currentlyFocused);
      if (index > -1) {
        getImage360Accepted().splice(index, 1);
      }
    }
  }

  // Undo delete
  undoDelete() {
    if (!isHovering()) return;
    getCurrentlyFocused().toDelete = false;
  }

  // Hide current view
  hideView() {
    if (!isHovering()) return;
    const currentlyFocused = getCurrentlyFocused();
    currentlyFocused.isHidden = true;
    document.getElementById('li-' + currentlyFocused.file).children[1].checked = true;
    this.dispatchEvent({ type: 'hidden-image', image: currentlyFocused });
  }

  // Undo hide
  undoHide() {
    if (!isHovering()) return;
    const currentlyFocused = getCurrentlyFocused();
    currentlyFocused.isHidden = false;
    document.getElementById('li-' + currentlyFocused.file).children[1].checked = false;
    this.dispatchEvent({ type: 'unhide-image', image: currentlyFocused });
  }

  // Export accepted images
  exportAccepted() {
    const image360Accepted = getImage360Accepted();

    for (let image of this.images) {
      if (!isInImage360Accepted(image) && !image.toDelete) {
        addToImage360Accepted(image);
      }
    }

    let text = 'File\tTime\tLong\tLat\tAlt\tcourse\tpitch\troll\n';

    for (let element of getImage360Accepted()) {
      let fileName = element.file;
      if (fileName.lastIndexOf('./') === -1) {
        fileName = fileName.slice(fileName.lastIndexOf('/'), fileName.length);
      } else {
        fileName = fileName.split('./').pop();
      }
      fileName = `"${fileName.slice(1)}"`;

      text += `${fileName}\t${element.time}\t${element.longitude}\t${element.latitude}\t${element.altitude}\t${element.course}\t${element.pitch}\t${element.roll}\n`;
    }

    const link = document.createElement('a');
    const file = new Blob([text], { type: 'text/plain' });
    link.href = URL.createObjectURL(file);
    link.download = 'coordinates.txt';
    link.click();
    URL.revokeObjectURL(link.href);

    this._downloadRotationDiffs();

    clearImage360Accepted();
  }

  // Load panorama texture
  load(image360) {
    return new Promise(async (resolve, reject) => {
      try {
        const headers = {};
        if (window.viewerToken) {
          headers['Authorization'] = `Bearer ${window.viewerToken}`;
        }

        const response = await fetch(image360.file, { headers });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        // Kompresja obrazu na urządzeniach mobilnych
        let imageBlob = blob;
        if (this.isMobile) {
          imageBlob = await this.compressImageForMobile(blob);
        }

        const objectURL = URL.createObjectURL(imageBlob);

        let texture = new THREE.TextureLoader().load(
          objectURL,
          () => {
            URL.revokeObjectURL(objectURL);
            resolve();
          },
          undefined,
          (err) => {
            URL.revokeObjectURL(objectURL);
            reject(err);
          }
        );
        texture.wrapS = THREE.RepeatWrapping;
        texture.repeat.x = -1;

        image360.texture = texture;
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Kompresuje obraz panoramy dla urządzeń mobilnych.
   * Zmniejsza rozdzielczość do maksymalnie 4096px szerokości (lub 2048px dla słabych urządzeń).
   * @param {Blob} blob - Oryginalny blob obrazu
   * @param {number} quality - Jakość kompresji JPEG (0-1)
   * @returns {Promise<Blob>} - Skompresowany blob lub oryginalny jeśli kompresja nie jest potrzebna
   */
  async compressImageForMobile(blob, quality = 0.85) {
    return new Promise((resolve) => {
      const img = new Image();
      const objectURL = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(objectURL);

        // Określ maksymalną szerokość na podstawie pamięci urządzenia
        let maxWidth = 4096; // domyślnie 4K
        if (navigator.deviceMemory && navigator.deviceMemory <= 4) {
          maxWidth = 2048; // dla urządzeń z ≤4GB RAM użyj 2K
        }

        // Jeśli obraz jest mniejszy niż maxWidth, nie kompresuj
        if (img.width <= maxWidth) {
          resolve(blob);
          return;
        }

        // Oblicz nowe wymiary zachowując proporcje
        const scale = maxWidth / img.width;
        const newWidth = maxWidth;
        const newHeight = Math.round(img.height * scale);

        // Utwórz canvas do kompresji
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Konwertuj na blob z kompresją JPEG
        canvas.toBlob(
          (compressedBlob) => {
            if (compressedBlob) {
              resolve(compressedBlob);
            } else {
              console.warn('[Images360] Mobile: Compression failed, using original');
              resolve(blob); // Fallback na oryginalny
            }

            // Wyczyść canvas
            canvas.width = 0;
            canvas.height = 0;
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectURL);
        console.warn('[Images360] Mobile: Failed to load image for compression, using original');
        resolve(blob); // Fallback na oryginalny
      };

      img.src = objectURL;
    });
  }

  // Handle saved array colors
  handleSavedArr() {
    const image360Accepted = getImage360Accepted();

    for (let image of this.images) {
      if (isInImage360Accepted(image)) {
        image.mesh.material = new THREE.MeshBasicMaterial({
          side: THREE.BackSide,
          color: 0x50c878,
        });
        const el = document.getElementById(image.file);
        if (el) el.classList.add('saved');
      } else if (image.toDelete) {
        image.mesh.material = new THREE.MeshBasicMaterial({
          side: THREE.BackSide,
          color: 0xff2800,
        });
      } else if (image.isHidden) {
        image.mesh.material = new THREE.MeshBasicMaterial({
          side: THREE.BackSide,
          color: 0x002299,
        });
      } else {
        image.mesh.material = new THREE.MeshBasicMaterial({
          side: THREE.BackSide,
          color: 0xffffff,
        });
      }
    }
  }

  // Handle hovering detection
  handleHovering() {
    let mouse = this.viewer.inputHandler.mouse;
    let camera = this.viewer.scene.getActiveCamera();
    let domElement = this.viewer.renderer.domElement;

    let ray = Potree.Utils.mouseToRay(
      mouse,
      camera,
      domElement.clientWidth,
      domElement.clientHeight
    );

    raycaster.ray.copy(ray);
    let intersections = raycaster.intersectObjects(this.node.children);
    let checkedIntersections = [];

    intersections.map(function (d) {
      if (d.object.name !== '') {
        checkedIntersections.push(d);
      }
    });

    if (checkedIntersections.length === 0) {
      this._resetOwnHover();
      return;
    }

    let intersection = checkedIntersections[0];

    const focused = getCurrentlyFocused();

    // When inside a focused panorama the first intersection is usually the
    // large focus-sphere itself.  Skip it only when it belongs to the focused
    // image; otherwise it's a valid ball from another instance.
    if (isHovering() && checkedIntersections.length > 1 &&
        intersection.object.image360 === focused) {
      intersection = checkedIntersections[1];
    }

    let hovered = intersection.object;

    if (hovered.image360 === focused || !hovered.image360.mesh.visible) {
      this._resetOwnHover();
      return;
    }

    if (this._ownLastHovered && this._ownLastHovered !== hovered) {
      this._restoreMaterial(this._ownLastHovered);
    }

    this._ownLastHovered = hovered;
    setCurrentlyHovered(hovered);
    hovered.material = sphereMaterialHovered;
  }

  _resetOwnHover() {
    if (this._ownLastHovered) {
      this._restoreMaterial(this._ownLastHovered);
      if (getCurrentlyHovered() === this._ownLastHovered) {
        setCurrentlyHovered(null);
      }
      this._ownLastHovered = null;
    }
  }

  _restoreMaterial(mesh) {
    if (!mesh) return;
    const img = mesh.image360;
    const editModeOn = this.getEditModeChecked();

    if (img && isInImage360Accepted(img)) {
      mesh.material = new THREE.MeshBasicMaterial({ side: THREE.FrontSide, color: 0x50c878 });
    } else if (img && img.toDelete && editModeOn) {
      mesh.material = new THREE.MeshBasicMaterial({ side: THREE.FrontSide, color: 0x990000 });
    } else if (img && img.isHidden && editModeOn) {
      mesh.material = new THREE.MeshBasicMaterial({ side: THREE.FrontSide, color: 0x002299 });
    } else if (img && img.isHidden && !editModeOn) {
      mesh.visible = false;
      mesh.material = sphereMarkerMaterial;
    } else {
      mesh.material = sphereMarkerMaterial;
    }
  }

  // Update loop
  update() {
    this.handleHovering();

    if (isHovering()) {
      this.renderSpheres();
    } else {
      this._syncIdlePanoramaMarkerVisibility();
    }
  }

  // Save rotation locally
  saveRotationLoc(image360) {
    let { course, pitch, roll } = this.focusedImage;
    image360.roll = roll + getRollLocal();
    image360.pitch = pitch - getPitchLocal();
    if (image360.type === 'leica') {
      image360.course = course - 180 - getCourseLocal();
    } else {
      image360.course = course - getCourseLocal();
    }
  }

  // Accept rotation changes
  async acceptRotation() {
    let check = false;
    const currentlyFocused = getCurrentlyFocused();

    if (isHovering() && currentlyFocused !== null) {
      // Capture old values before saveRotationLoc mutates them
      const oldCourse = currentlyFocused.course;
      const oldPitch = currentlyFocused.pitch;
      const oldRoll = currentlyFocused.roll;
      const oldPosition = currentlyFocused.position ? [...currentlyFocused.position] : undefined;

      this.saveRotationLoc(currentlyFocused);
      currentlyFocused.position[0] += getPosXLocal();
      currentlyFocused.position[1] += getPosYLocal();
      currentlyFocused.position[2] += getPosZLocal();

      const [newLong, newLat, newAlt] = currentlyFocused.position;
      currentlyFocused.longitude = newLong;
      currentlyFocused.latitude = newLat;
      currentlyFocused.altitude = newAlt;

      if (!this._rotationDiffs) this._rotationDiffs = [];
      this._rotationDiffs.push({
        file: currentlyFocused.file,
        type: currentlyFocused.type,
        name: currentlyFocused.name,
        oldCourse, oldPitch, oldRoll,
        oldPosition,
        newCourse: currentlyFocused.course,
        newPitch: currentlyFocused.pitch,
        newRoll: currentlyFocused.roll,
        newPosition: currentlyFocused.position ? [...currentlyFocused.position] : undefined,
      });

      const index = indexInImage360Accepted(currentlyFocused);
      if (index > -1) {
        replaceInImage360Accepted(index, currentlyFocused);
        check = true;
      }

      if (!check) {
        addToImage360Accepted(currentlyFocused);
      }

      // Call backend callback if provided
      if (this._callbacks?.onSaveRotation) {
        try {
          const course = currentlyFocused.course;
          const pitch = currentlyFocused.pitch;
          const roll = currentlyFocused.roll;
          const position = currentlyFocused.position ? [...currentlyFocused.position] : undefined;

          await this._callbacks.onSaveRotation(
            this._extractImage360Data(currentlyFocused),
            { course, pitch, roll, position }
          );
        } catch (error) {
          console.warn('[Images360] Backend save failed:', error);
        }
      }
    }
    this._clearPendingDelta(currentlyFocused);
    this.resetRotValues();
    this.viewer.dispatchEvent({ type: 'panorama_deltas_changed' });
  }

  // Get currently focused image
  getCurrentlyFocused() {
    return getCurrentlyFocused();
  }

  isImageAccepted(image) {
    return isInImage360Accepted(image);
  }

  // Set callbacks for backend operations
  setCallbacks(callbacks) {
    this._callbacks = callbacks;
  }

  _downloadRotationDiffs() {
    const diffs = this._rotationDiffs;
    if (!diffs || diffs.length === 0) return;

    const pad = (v, d) => v.toFixed(d).padStart(14);
    const lines = [
      `Panorama rotation diffs — ${new Date().toISOString()}`,
      `Modified: ${diffs.length} panorama(s)`,
      ``,
    ];

    for (const d of diffs) {
      const dC = d.newCourse - d.oldCourse;
      const dP = d.newPitch - d.oldPitch;
      const dR = d.newRoll - d.oldRoll;
      lines.push(
        `--- ${d.file || '?'} (type: ${d.type || '?'}, name: ${d.name || '?'}) ---`,
        `               course         pitch          roll`,
        `  old:   ${pad(d.oldCourse, 4)}  ${pad(d.oldPitch, 4)}  ${pad(d.oldRoll, 4)}`,
        `  new:   ${pad(d.newCourse, 4)}  ${pad(d.newPitch, 4)}  ${pad(d.newRoll, 4)}`,
        `  delta: ${pad(dC, 4)}  ${pad(dP, 4)}  ${pad(dR, 4)}`,
        `               X              Y              Z`,
        `  old:   ${pad(d.oldPosition?.[0] ?? 0, 6)}  ${pad(d.oldPosition?.[1] ?? 0, 6)}  ${pad(d.oldPosition?.[2] ?? 0, 6)}`,
        `  new:   ${pad(d.newPosition?.[0] ?? 0, 6)}  ${pad(d.newPosition?.[1] ?? 0, 6)}  ${pad(d.newPosition?.[2] ?? 0, 6)}`,
        ``,
      );
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rotation-diffs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    this._rotationDiffs = [];
  }

  // Extract Image360 data for serialization
  _extractImage360Data(image360) {
    return {
      file: image360.file,
      course: image360.course,
      pitch: image360.pitch,
      roll: image360.roll,
      longitude: image360.longitude,
      latitude: image360.latitude,
      altitude: image360.altitude,
      type: image360.type,
      name: image360.name,
      isHidden: image360.isHidden,
      toDelete: image360.toDelete
    };
  }

  /**
   * Czyści panoramy Trimble z sceny i mapy
   */
  cleanupTrimbleImages() {
    const trimbleImages = this.images.filter(img => img && img.type === 'trimble');
    if (trimbleImages.length === 0) return;

    // 1. Usuń z mapy (minimapka)
    if (this.viewer.mapView && this.viewer.mapView.getImages360Layer) {
      const mapLayer = this.viewer.mapView.getImages360Layer();
      if (mapLayer) {
        const source = mapLayer.getSource();
        const features = source.getFeatures();

        // Znajdź features związane z tymi panoramami Trimble
        const featuresToRemove = [];

        for (const feature of features) {
          const image360 = feature.get('image360');
          const images360 = feature.get('images360');

          // Sprawdź czy feature należy do Trimble i do tej instancji Images360
          if (image360 && image360.type === 'trimble' && images360 === this) {
            featuresToRemove.push(feature);
          }
        }

        // Usuń wszystkie znalezione features
        featuresToRemove.forEach(feature => {
          source.removeFeature(feature);
        });
      }
    }

    // 2. Usuń z sceny (mesh, texture)
    for (const image of trimbleImages) {
      if (image.mesh) {
        this.node.remove(image.mesh);
        // Dispose geometry and material
        if (image.mesh.geometry) {
          image.mesh.geometry.dispose();
        }
        if (image.mesh.material) {
          if (image.mesh.material.map) {
            image.mesh.material.map.dispose();
          }
          image.mesh.material.dispose();
        }
        image.mesh = null;
      }

      // Dispose texture if exists separately
      if (image.texture) {
        image.texture.dispose();
        image.texture = null;
      }
    }

    // 3. Usuń z tablicy images
    for (const image of trimbleImages) {
      const index = this.images.indexOf(image);
      if (index > -1) {
        this.images.splice(index, 1);
      }
    }

    // 4. Usuń z imagesSourceUnique i _visible
    const trimbleName = 'trimble';
    const uniqueIndex = this.imagesSourceUnique.indexOf(trimbleName);
    if (uniqueIndex > -1) {
      this.imagesSourceUnique.splice(uniqueIndex, 1);
      this._visible.splice(uniqueIndex, 1);
    }

    // 5. Wywołaj event o usunięciu
    this.viewer.scene.dispatchEvent({
      type: '360_images_removed',
      scene: this.viewer.scene,
      images: this,
    });
  }

}
